import React, { PropsWithChildren } from 'react';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import Button from '@components-clean/core/button';
import { DateTimePickerController } from '@components-clean/core/datetime-picker';

const DateTimePickers = () => {
    const { control, handleSubmit: handleSubmitHookForm } = useForm<IForm>({
        mode: 'all',
    });

    interface IFormSectionProps extends PropsWithChildren {
        title: string;
    }

    const FormSection = ({ title, children }: IFormSectionProps) => (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                minWidth: 150,
                maxWidth: 350,
            }}
        >
            <h2 style={{ margin: 0 }}>{title}</h2>
            {children}
        </div>
    );

    interface IForm {
        solidOptional: Date;
        solidRequired: Date;
        solidPlaceholderOptional: Date;
        solidMinOptional: Date;
        solidMaxOptional: Date;
        solidReadonlyOptional: Date;
        solidDisabled: Date;
        flatOptional: Date;
        flatRequired: Date;
        flatPlaceholderOptional: Date;
        flatFutureOptional: Date;
        flatPastOptional: Date;
        flatReadonlyOptional: Date;
        flatDisabled: Date;
    }

    return (
        <div className="k-pb-8">
            <form
                // eslint-disable-next-line no-alert -- Allow alert on Dummy Pages.
                onSubmit={handleSubmitHookForm((values) => alert(JSON.stringify(values, null, 2)))}
                noValidate
                className="k-d-flex-col k-gap-8"
                style={{ width: 'fit-content' }}
            >
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <FormSection title="Solid">
                        <DateTimePickerController
                            name="solidOptional"
                            label="DateTime Picker - Solid, Optional"
                            data-testid="date-time-picker-solid-optional"
                            fillMode="solid"
                            control={control}
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidRequired"
                            label="DateTime Picker - Solid, Required"
                            data-testid="date-time-picker-solid-required"
                            fillMode="solid"
                            required
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidPlaceholderOptional"
                            label="DateTime Picker - Solid, Placeholder, Optional"
                            data-testid="date-time-picker-solid-placeholder-optional"
                            fillMode="solid"
                            placeholder="Placeholder"
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidMinOptional"
                            label="DateTime Picker - Solid, Min, Optional"
                            data-testid="date-time-picker-solid-min-optional"
                            fillMode="solid"
                            min={moment().toDate()}
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidMaxOptional"
                            label="DateTime Picker - Solid, Max, Optional"
                            data-testid="date-time-picker-solid-max-optional"
                            fillMode="solid"
                            max={moment().toDate()}
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidReadonlyOptional"
                            defaultValue={new Date()}
                            label="DateTime Picker - Solid, Readonly, Optional"
                            data-testid="date-time-picker-solid-readonly-optional"
                            readonly
                            fillMode="solid"
                        />

                        <DateTimePickerController
                            control={control}
                            name="solidDisabled"
                            defaultValue={new Date()}
                            label="DateTime Picker - Solid, Disabled"
                            data-testid="date-time-picker-solid-disabled"
                            disabled
                            fillMode="solid"
                        />
                    </FormSection>
                    <FormSection title="Flat">
                        <DateTimePickerController
                            control={control}
                            name="flatOptional"
                            label="DateTime Picker - Flat, Optional"
                            data-testid="date-picker-flat-optional"
                            fillMode="flat"
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatRequired"
                            label="DateTime Picker - Flat, Required"
                            data-testid="date-time-picker-flat-required"
                            fillMode="flat"
                            required
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatPlaceholderOptional"
                            label="DateTime Picker - Flat, Optional"
                            data-testid="date-time-picker-flat-placeholder-optional"
                            fillMode="flat"
                            placeholder="Placeholder"
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatFutureOptional"
                            label="DateTime Picker - Flat, Future, Optional"
                            data-testid="date-time-picker-flat-future-optional"
                            fillMode="flat"
                            min="future"
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatPastOptional"
                            label="DateTime Picker - Flat, Past, Optional"
                            data-testid="date-time-picker-flat-past-optional"
                            fillMode="flat"
                            max="past"
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatReadonlyOptional"
                            defaultValue={new Date()}
                            label="DateTime Picker - Flat, Readonly, Optional"
                            data-testid="date-time-picker-flat-readonly-optional"
                            readonly
                            fillMode="flat"
                        />

                        <DateTimePickerController
                            control={control}
                            name="flatDisabled"
                            defaultValue={new Date()}
                            label="DateTime Picker - Flat, Disabled"
                            data-testid="date-time-picker-flat-disabled"
                            disabled
                            fillMode="flat"
                        />
                    </FormSection>
                </div>
                <Button data-testid="submit" primary>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default DateTimePickers;
